/**
 * Pure HTML rendering helpers for the Viewing Deck tab.
 * Extracted from viewingDeck.ts to keep that file under the 300-line limit.
 */

import { esc } from '../../../shared/rendering.js'
import { renderEventCard } from '../../components/eventCard.js'
import { renderTaskList } from '../../components/taskList.js'
import { renderSection } from '../../components/section.js'
import { formatDuration } from '../../../types/domain.js'
import type { ParsedEvent, TaskList, FileTouched } from '../../../types/api.js'
import type { RunState } from '../../../types/api-secondary.js'
import type { EventCardProps } from '../../../types/components.js'

// ─── Event List ───────────────────────────────────────────────────────────────

export function renderEventList(
  events: readonly ParsedEvent[],
  filter: string | undefined,
): string {
  const filtered = filter ? events.filter((ev) => eventKind(ev.event_type) === filter) : events
  if (filtered.length === 0) {
    return '<div class="empty">No events match the current filter</div>'
  }
  return groupRepeatedEvents(filtered)
    .map(({ event, count }) => renderEventCard(buildEventCardProps(event, count)))
    .join('')
}

function groupRepeatedEvents(
  events: readonly ParsedEvent[],
): { event: ParsedEvent; count: number }[] {
  const grouped: { event: ParsedEvent; count: number }[] = []
  for (const event of events) {
    const last = grouped.at(-1)
    if (last && repeatKey(last.event) === repeatKey(event)) {
      last.count += 1
    } else {
      grouped.push({ event, count: 1 })
    }
  }
  return grouped
}

function repeatKey(ev: ParsedEvent): string {
  return `${ev.event_type}|${ev.stage ?? ''}|${ev.iteration ?? ''}|${ev.summary ?? ''}`
}

function eventKind(eventType: string): string {
  if (eventType === 'tool' || eventType.includes('Tool')) return 'tool'
  if (eventType === 'llm' || eventType.includes('LLM')) return 'llm'
  if (eventType === 'stage' || eventType.startsWith('Stage')) return 'stage'
  if (eventType.startsWith('Agent')) return 'agent'
  return eventType
}

export function buildEventCardProps(ev: ParsedEvent, repeatCount = 1): EventCardProps {
  const kind = eventKind(ev.event_type)
  const display = describeEvent(ev, kind)
  const phaseClassMap: Record<string, string> = {
    implementer: 'impl',
    critic: 'critic',
    'bdd-reviewer': 'critic',
    'product-manager': 'pm',
    reflect: 'reflect',
    introspect: 'reflect',
  }
  return {
    icon: display.icon,
    iconClass: display.iconClass,
    title: display.title,
    stage: humanizeStage(ev.stage),
    stageClass:
      ev.stage !== null && ev.stage !== undefined && ev.stage in phaseClassMap
        ? (phaseClassMap[ev.stage] ?? '')
        : '',
    iteration: ev.iteration ?? null,
    duration:
      ev.duration_s !== null && ev.duration_s !== undefined
        ? (formatDuration(ev.duration_s) ?? '')
        : '',
    timestamp: formatEventTime(ev.timestamp),
    content: esc(displaySummary(ev.summary, display.title, kind)),
    detailHtml: renderEventDetails(ev),
    detailLabel: detailLabelFor(ev),
    repeatCount,
  }
}

function describeEvent(
  ev: ParsedEvent,
  kind: string,
): { icon: string; iconClass: string; title: string } {
  if (kind === 'llm') {
    return ev.event_type.includes('Thinking') || ev.thinking?.text
      ? { icon: '…', iconClass: 'thinking', title: 'Thinking' }
      : { icon: '✦', iconClass: 'assistant', title: 'Assistant' }
  }

  if (kind === 'stage') {
    const stage = humanizeStage(ev.stage) || 'step'
    if (ev.event_type.includes('Completed'))
      return { icon: '✓', iconClass: 'st', title: `Finished ${stage}` }
    if (ev.event_type.includes('Failed'))
      return { icon: '!', iconClass: 'err', title: `Failed ${stage}` }
    return { icon: '•', iconClass: 'st', title: `Started ${stage}` }
  }

  if (kind === 'agent') {
    if (ev.event_type.includes('Completed'))
      return { icon: '✓', iconClass: 'st', title: 'Run completed' }
    if (ev.event_type.includes('Failed'))
      return { icon: '!', iconClass: 'err', title: 'Run failed' }
    return { icon: '•', iconClass: 'st', title: 'Run started' }
  }

  if (kind === 'tool') {
    const tool = ev.tool ?? toolFromSummary(ev.summary) ?? 'Tool'
    const normalized = tool.toLowerCase()
    if (normalized === 'read' && ev.file_read?.path) {
      return { icon: 'Read', iconClass: 'read', title: `Read ${ev.file_read.path}` }
    }
    if ((normalized === 'edit' || normalized === 'write') && ev.diff?.file) {
      return { icon: tool, iconClass: 'edit', title: `${tool} ${ev.diff.file}` }
    }
    if ((normalized === 'bash' || normalized === 'shell') && ev.code_block?.command) {
      return { icon: 'Bash', iconClass: 'bash', title: `Ran ${ev.code_block.command}` }
    }
    return { icon: tool, iconClass: toolIconClass(tool, ev.is_modifying), title: `Used ${tool}` }
  }

  return { icon: '•', iconClass: 'ev', title: humanizeEventType(ev.event_type) }
}

function toolIconClass(tool: string, isModifying: boolean | undefined): string {
  const normalized = tool.toLowerCase()
  if (normalized === 'read' || normalized === 'grep' || normalized === 'glob') return 'read'
  if (normalized === 'bash' || normalized === 'shell') return 'bash'
  if (normalized === 'kb' || normalized.includes('kb')) return 'kb'
  if (isModifying || normalized === 'edit' || normalized === 'write') return 'edit'
  return 'tool'
}

function toolFromSummary(summary: string | undefined): string | null {
  const match = summary?.match(/^(Read|Edit|Write|Bash|Grep|Glob|Search)\b/i)
  return match?.[1] ?? null
}

function humanizeStage(stage: string | null | undefined): string {
  if (!stage) return ''
  return stage
    .split('-')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')
}

function humanizeEventType(eventType: string): string {
  return eventType
    .replace(/^LLM/, 'Assistant ')
    .replace(/^Tool/, '')
    .replace(/^Stage/, '')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .trim()
}

/** "HH:MM:SS" in UTC — same invariant as formatTime(), but with seconds. */
function formatEventTime(timestamp: string): string {
  const date = new Date(timestamp)
  if (Number.isNaN(date.getTime())) return timestamp
  const h = date.getUTCHours().toString().padStart(2, '0')
  const m = date.getUTCMinutes().toString().padStart(2, '0')
  const s = date.getUTCSeconds().toString().padStart(2, '0')
  return `${h}:${m}:${s}`
}

function detailLabelFor(ev: ParsedEvent): string {
  if (ev.code_block?.output) return 'Show output'
  if (ev.diff) return 'Show diff'
  if (ev.thinking?.text) return 'Show reasoning'
  return 'Show details'
}

function displaySummary(summary: string | undefined, fallback: string, kind: string): string {
  const text = kind === 'agent' ? fallback : (summary ?? fallback)
  if (kind !== 'llm') return text
  return text
    .replace(/\bLLM\s+response\b/gi, 'Assistant response')
    .replace(/\bLLM\s+call(s?)\b/gi, 'Assistant call$1')
    .replace(/\bLLM\b/g, 'Assistant')
}

function renderEventDetails(ev: ParsedEvent): string {
  const rows: string[] = []
  if (ev.tool) rows.push(detailRow('Tool', ev.tool))
  if (ev.turn !== null) rows.push(detailRow('Turn', String(ev.turn)))
  if (ev.thinking?.text) rows.push(detailBlock('Thinking', ev.thinking.text))
  if (ev.code_block) {
    rows.push(detailRow('Command', ev.code_block.command))
    if (ev.code_block.output) rows.push(detailBlock('Output', ev.code_block.output))
  }
  if (ev.file_read) rows.push(detailBlock(`Read ${ev.file_read.path}`, ev.file_read.lines))
  if (ev.diff) rows.push(detailBlock(`Diff ${ev.diff.file}`, ev.diff.content))
  if (ev.error) rows.push(detailBlock('Error', ev.error.message))
  return rows.length > 0 ? `<div class="ev-detail-body">${rows.join('')}</div>` : ''
}

function detailRow(label: string, value: string): string {
  return `<div class="si"><span class="si-txt">${esc(label)}</span><span style="font-family:var(--mono);font-size:10px;color:var(--t2)">${esc(value)}</span></div>`
}

function detailBlock(label: string, value: string): string {
  return `<div class="ev-detail-block"><div class="ev-detail-label">${esc(label)}</div><pre>${esc(value.slice(0, 4000))}</pre></div>`
}

// ─── Sidebar ──────────────────────────────────────────────────────────────────

export function renderSidebarContent(
  taskLists: readonly TaskList[] | null,
  filesTouched: readonly FileTouched[],
  kbItems: readonly { entry_type: string; title: string }[],
  context: Pick<RunState, 'current_stage' | 'subagents'> | null = null,
): string {
  const parts: string[] = []

  if (context?.current_stage || context?.subagents?.length) {
    const rows: string[] = []
    if (context.current_stage) {
      rows.push(
        `<div class="si"><span class="si-txt">Current stage</span><span style="font-family:var(--mono);font-size:10px;color:var(--cyn)">${esc(context.current_stage)}</span></div>`,
      )
    }
    for (const agent of context.subagents ?? []) {
      rows.push(
        `<div class="si"><span class="si-txt">${esc(agent.subagent_type)}</span><span style="font-family:var(--mono);font-size:10px;color:var(--t3)">${esc(agent.status)}${agent.duration_s !== null ? ` · ${esc(formatDuration(agent.duration_s) ?? '')}` : ''}</span></div>`,
      )
    }
    parts.push(
      renderSection({ title: 'Current context', content: rows.join(''), defaultOpen: true }),
    )
  }

  if (taskLists && taskLists.length > 0) {
    const taskHtml = taskLists
      .map((tl) =>
        renderTaskList({
          title: tl.title,
          progress: `${tl.completed}/${tl.total} (${tl.total > 0 ? Math.round((tl.completed / tl.total) * 100) : 0}%)`,
          items: tl.items.map((i) => ({
            text: i.text,
            status: i.status,
            duration: formatDuration(i.duration_s),
          })),
        }),
      )
      .join('')
    parts.push(renderSection({ title: 'Tasks', content: taskHtml, defaultOpen: true }))
  }

  if (filesTouched.length > 0) {
    const filesHtml = filesTouched
      .map(
        (f) =>
          `<div class="si"><span class="si-txt" style="flex:1;font-family:var(--mono);font-size:11px">${esc(f.path)}</span><span style="font-family:var(--mono);font-size:10px;color:var(--t4)">${esc(f.op)}</span></div>`,
      )
      .join('')
    parts.push(
      renderSection({
        title: 'Files touched',
        badge: String(filesTouched.length),
        content: filesHtml,
        defaultOpen: true,
      }),
    )
  }

  if (kbItems.length > 0) {
    const kbHtml = kbItems
      .map(
        (kb) =>
          `<div class="si"><span class="si-txt" style="flex:1">${esc(kb.title)}</span><span style="font-family:var(--mono);font-size:10px;color:var(--t4)">${esc(kb.entry_type)}</span></div>`,
      )
      .join('')
    parts.push(
      renderSection({
        title: 'KB items',
        badge: String(kbItems.length),
        content: kbHtml,
        defaultOpen: false,
      }),
    )
  }

  return parts.length > 0
    ? parts.join('')
    : '<div class="empty">Waiting for state updates…<br><span style="color:var(--t4);font-size:11px">Live events are streaming in the deck.</span></div>'
}
